import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import { Layout } from '@/shared/components/Layout';
import { api } from '@/shared/lib/api';
import { trackEvent, captureError } from '@/shared/lib/monitoring';
import { useGuidedTourStore } from '@/features/guided-tour';
import { useIsDesktop } from '@/shared/hooks/useIsDesktop';
import { useTTS } from '@/shared/hooks/useTTS';
import { renderHighlightedSentence } from '@/shared/lib/highlight';
import { addPhoneticStress } from '@/shared/lib/phonetics';
import { useBackHandler } from '@/shared/lib/navigation/BackNavigationContext';
import { useOnTabActive, useIsTabActive } from '@/shared/components/MainTabsKeepAlive';

interface Word {
  id: number;
  original_text: string;
  main_translation_text: string;
  srs_level: number;
  mc_en_ru_progress: number;
  mc_ru_en_progress: number;
  input_en_ru_done: boolean;
  input_ru_en_done: boolean;
  error_count: number;
  error_streak: number;
  success_count: number;
  next_review_at: string;
  is_hidden: boolean;
  plural_form?: string | null;
  source_course_id?: number | null;
  translations: Array<{ id: number; translation_text: string }>;
  examples: Array<{ id: number; example_sentence: string; example_translation: string }>;
  folder_ids?: number[];
  transcription?: string | null;
  russian_phonetic?: string | null;
  cefr_level?: string | null;
  part_of_speech?: string | null;
  is_phrase?: boolean;
}

interface Folder {
  id: number;
  name: string;
  word_count: number;
}

type FilterMode = 'all' | 'mc' | 'input' | 'srs' | 'hidden';

interface PendingDeletion {
  id: number;
  word: Word;
  timer: ReturnType<typeof setTimeout>;
}

interface WordsResponse {
  words: Word[];
  total: number;
  page: number;
  pages: number;
}

type WordMode = 'mc' | 'input' | 'srs';

function declensionWords(n: number): string {
  const lastDigit = n % 10;
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return `${n} слов`;
  if (lastDigit === 1) return `${n} слово`;
  if (lastDigit >= 2 && lastDigit <= 4) return `${n} слова`;
  return `${n} слов`;
}

function getWordMode(word: Word, mcReps: number = 1): WordMode {
  if (word.srs_level > 0) return 'srs';
  const reqReps = Math.max(mcReps, 1 + (word.translations?.length ?? 0));
  if (word.mc_en_ru_progress >= reqReps && word.mc_ru_en_progress >= reqReps) return 'input';
  return 'mc';
}

function getSrsColor(level: number): string {
  return `var(--theme-srs-${Math.min(level, 9)})`;
}

function getModeLabel(mode: WordMode): string {
  switch (mode) {
    case 'mc': return 'MC';
    case 'input': return 'Ввод';
    case 'srs': return 'SRS';
  }
}

function getModeColor(mode: WordMode): string {
  switch (mode) {
    case 'mc': return 'var(--mode-mc)';
    case 'input': return 'var(--mode-input)';
    case 'srs': return 'var(--mode-srs)';
  }
}

// === SVG иконки для свайп-кнопок ===
const SwipeIconHide = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);
const SwipeIconEdit = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const SwipeIconDelete = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

// === Swipe actions (Telegram-style) ===
function SwipeActions({ children, enabled, onHide, onEdit, onDelete }: {
  children: React.ReactNode;
  enabled: boolean;
  onHide: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const BUTTONS_WIDTH = 150;
  const x = useMotionValue(0);
  const [isOpen, setIsOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Прогресс раскрытия: 0..1
  const progress = useTransform(x, [-BUTTONS_WIDTH, 0], [1, 0]);
  const buttonsOpacity = useTransform(x, [-BUTTONS_WIDTH * 0.3, -BUTTONS_WIDTH * 0.6], [0, 1]);

  // Каждая кнопка выезжает с задержкой
  const btn1X = useTransform(x, [-BUTTONS_WIDTH, 0], [0, 50]);
  const btn2X = useTransform(x, [-BUTTONS_WIDTH, 0], [0, 80]);
  const btn3X = useTransform(x, [-BUTTONS_WIDTH, 0], [0, 110]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleAction = useCallback((action: () => void) => {
    close();
    action();
  }, [close]);

  return (
    <div
      ref={cardRef}
      style={{ position: 'relative', overflow: 'hidden', borderRadius: 'var(--theme-border-radius, 12px)' }}
    >
      {/* Кнопки — абсолютно позиционированы справа, скрыты пока не свайпнут */}
      <motion.div style={{
        position: 'absolute', top: 0, bottom: 0, right: 0,
        width: BUTTONS_WIDTH, display: 'flex',
        opacity: buttonsOpacity,
      }}>
        {[
          { icon: <SwipeIconHide />, bg: '#D97706', action: onHide, x: btn1X },
          { icon: <SwipeIconEdit />, bg: '#2563EB', action: onEdit, x: btn2X },
          { icon: <SwipeIconDelete />, bg: '#DC2626', action: onDelete, x: btn3X },
        ].map((btn, i) => (
          <motion.button
            key={i}
            onClick={() => handleAction(btn.action)}
            style={{
              x: btn.x,
              flex: 1, border: 'none', cursor: 'pointer',
              background: btn.bg, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 0,
            }}
          >
            {btn.icon}
          </motion.button>
        ))}
      </motion.div>

      {/* Контент */}
      <motion.div
        drag={enabled ? "x" : false}
        dragConstraints={{ left: -BUTTONS_WIDTH, right: 0 }}
        dragElastic={0.05}
        style={{ x, position: 'relative', zIndex: 1 }}
        onDragEnd={(_, info) => {
          const threshold = BUTTONS_WIDTH * 0.35;
          if (info.offset.x < -threshold || info.velocity.x < -200) {
            animate(x, -BUTTONS_WIDTH, { type: 'spring', stiffness: 500, damping: 35 });
            setIsOpen(true);
          } else {
            animate(x, 0, { type: 'spring', stiffness: 500, damping: 35 });
            setIsOpen(false);
          }
        }}
      >
        <div onClick={() => { if (isOpen) { animate(x, 0, { type: 'spring', stiffness: 500, damping: 35 }); setIsOpen(false); } }}>
          {children}
        </div>
      </motion.div>
    </div>
  );
}

// Подсветка совпадения поискового запроса в тексте (с учётом ё→е)
function HighlightText({ text, query }: { text: string | null | undefined; query: string }): ReactNode {
  if (!text) return '';
  if (!query || !query.trim()) return text;
  const normalizedQuery = query.trim().toLowerCase().replace(/ё/g, 'е');
  const normalizedText = text.toLowerCase().replace(/ё/g, 'е');
  if (!normalizedQuery || !normalizedText.includes(normalizedQuery)) return text;

  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  let searchStart = 0;

  while (true) {
    const matchIndex = normalizedText.indexOf(normalizedQuery, searchStart);
    if (matchIndex === -1) break;

    const before = text.slice(lastIndex, matchIndex);
    const match = text.slice(matchIndex, matchIndex + normalizedQuery.length);
    if (before) result.push(before);
    result.push(
      <span key={matchIndex} style={{ background: 'rgba(255, 215, 0, 0.35)', fontWeight: 700, borderRadius: 2 }}>
        {match}
      </span>
    );
    lastIndex = matchIndex + normalizedQuery.length;
    searchStart = lastIndex;
  }

  if (lastIndex < text.length) result.push(text.slice(lastIndex));
  return <span>{result}</span>;
}

function WordCard({ word, index, expanded, onToggleExpand, onEdit, onDelete, onToggleHidden, onReset, folders = [], onFolderToggle, mcReps = 1, selectionMode = false, selected = false, onSelect, searchQuery, onOpenCreateFolder }: {
  word: Word;
  index: number;
  expanded: boolean;
  onToggleExpand: () => void;
  onEdit: (word: Word) => void;
  onDelete: (id: number) => void;
  onToggleHidden: (id: number, is_hidden: boolean) => void;
  onReset: (id: number) => void;
  folders?: Folder[];
  onFolderToggle?: (wordId: number, folderId: number, adding: boolean) => void;
  mcReps?: number;
  selectionMode?: boolean;
  selected?: boolean;
  onSelect?: (id: number) => void;
  searchQuery?: string;
  onOpenCreateFolder?: () => void;
}) {
  const { speak } = useTTS();
  const showDetails = expanded;
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const mode = getWordMode(word, mcReps);

  // Прогресс: MC 0-40%, Input 40-60%, SRS 60-100%
  const mcProgress = Math.min(
    ((word.mc_en_ru_progress + word.mc_ru_en_progress) / (mcReps * 2)) * 40,
    40,
  );
  const inputProgress = word.srs_level > 0 ? 20 :
    (((word.input_en_ru_done ? 1 : 0) + (word.input_ru_en_done ? 1 : 0)) / 2) * 20;
  const srsProgress = (Math.min(word.srs_level, 9) / 9) * 40;
  const totalProgress = mcProgress + inputProgress + srsProgress;

  const formatNextReview = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    if (diff <= 0) return 'Сейчас';
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) return 'Менее часа';
    if (hours < 24) return `${hours}ч`;
    const days = Math.floor(hours / 24);
    return `${days}д`;
  };

  const formatNextReviewShort = (dateStr: string): { text: string; isDue: boolean } => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    if (diff <= 0) return { text: 'сейчас', isDue: true };
    const days = Math.floor(diff / 86400000);
    if (days === 0) return { text: 'сегодня', isDue: false };
    if (days === 1) return { text: 'завтра', isDue: false };
    if (days === 2) return { text: 'через день', isDue: false };
    if (days < 30) {
      const lastDigit = days % 10;
      const lastTwo = days % 100;
      let text: string;
      if (lastTwo >= 11 && lastTwo <= 14) text = `через ${days} дн.`;
      else if (lastDigit === 1) text = `через ${days} дн.`;
      else if (lastDigit >= 2 && lastDigit <= 4) text = `через ${days} дн.`;
      else text = `через ${days} дн.`;
      return { text, isDue: false };
    }
    const months = Math.floor(days / 30);
    const lastDigit = months % 10;
    const lastTwo = months % 100;
    let text: string;
    if (lastTwo >= 11 && lastTwo <= 14) text = `через ${months} мес.`;
    else if (lastDigit === 1) text = `через ${months} мес.`;
    else if (lastDigit >= 2 && lastDigit <= 4) text = `через ${months} мес.`;
    else text = `через ${months} мес.`;
    return { text, isDue: false };
  };

  return (
    <div style={{ display: 'flex', alignItems: 'stretch', marginBottom: 6, opacity: word.is_hidden ? 0.5 : 1 }}>
      {/* Номер */}
      <div style={{
        flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 28, fontSize: 12, color: 'var(--theme-text-muted)', userSelect: 'none',
      }}>
        {index}
      </div>

      <div
        className="card"
        style={{
          flex: 1, minWidth: 0, overflow: 'hidden', padding: '10px 14px',
          outline: selected ? '2px solid var(--theme-primary)' : 'none',
          transition: 'outline 0.15s',
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', minWidth: 0 }}
          onClick={() => selectionMode ? onSelect?.(word.id) : onToggleExpand()}
        >
          {/* Чекбокс в режиме выбора */}
          {selectionMode && (
            <div style={{
              width: 20, height: 20, borderRadius: 5, border: `2px solid ${selected ? 'var(--theme-primary)' : 'var(--theme-border)'}`,
              background: selected ? 'var(--theme-primary)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              {selected && <span style={{ color: 'var(--theme-primary-text)', fontSize: 12, lineHeight: 1 }}>✓</span>}
            </div>
          )}
          {/* SRS level indicator */}
          <div style={{
            width: 6, height: 34, borderRadius: 3, flexShrink: 0,
            background: mode === 'srs' ? getSrsColor(word.srs_level) : getModeColor(mode),
          }} />

          {/* Word info */}
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <div style={{
              fontWeight: 600, fontSize: 15, color: 'var(--theme-text)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <HighlightText text={word.original_text} query={searchQuery || ''} />
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  speak(word.original_text);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px 4px',
                  fontSize: 14,
                  lineHeight: 1,
                  color: 'var(--theme-text-muted)',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
                title="Озвучить слово"
                aria-label="Озвучить слово"
              >
                🔊
              </button>
            </div>
            <div style={{
              fontSize: 12, color: 'var(--theme-text-secondary)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
            }}>
              <HighlightText text={word.main_translation_text} query={searchQuery || ''} />
              {(() => {
                const query = (searchQuery || '').trim().toLowerCase().replace(/ё/g, 'е');
                if (!query) return null;
                const mainMatch = (word.main_translation_text || '').toLowerCase().replace(/ё/g, 'е').includes(query);
                const origMatch = (word.original_text || '').toLowerCase().replace(/ё/g, 'е').includes(query);
                if (mainMatch || origMatch) return null;
                const matched = (word.translations || [])
                  .filter(t => (t.translation_text || '').toLowerCase().replace(/ё/g, 'е').includes(query))
                  .slice(0, 1);
                if (matched.length === 0) return null;
                return (
                  <>
                    {' • '}
                    <HighlightText text={matched[0].translation_text} query={searchQuery || ''} />
                  </>
                );
              })()}
            </div>
          </div>

          {/* Error icons + Mode badge */}
          <div style={{ width: 90, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: mode === 'srs' ? 4 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {word.error_count >= 5 && <span style={{ fontSize: 11 }}>🔥</span>}
              {word.error_streak >= 3 && <span style={{ fontSize: 11 }}>⚠️</span>}
              <span className="srs-badge" style={{ background: getModeColor(mode) }}>
                {mode === 'srs' ? `${getModeLabel(mode)} ${word.srs_level}` : getModeLabel(mode)}
              </span>
            </div>
            {mode === 'srs' && (() => {
              const { text, isDue } = formatNextReviewShort(word.next_review_at);
              return (
                <span style={{ fontSize: 10, color: isDue ? 'var(--theme-error)' : 'var(--theme-text-muted)', lineHeight: 1.2 }}>
                  {text}
                </span>
              );
            })()}
          </div>
        </div>

      {/* Expanded details */}
      {showDetails && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--theme-border)' }}>
          {/* Фонетика и метаданные */}
          {(word.transcription || word.russian_phonetic || word.cefr_level || word.part_of_speech) && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 12,
              padding: '6px 10px',
              background: 'var(--theme-bg)',
              borderRadius: 8,
            }}>
              {word.transcription && (
                <span style={{ fontSize: 13, color: 'var(--theme-text-muted)', fontFamily: 'monospace' }}>
                  {word.transcription}
                </span>
              )}
              {word.transcription && word.russian_phonetic && (
                <span style={{ color: 'var(--theme-border)', fontSize: 12 }}>·</span>
              )}
              {word.russian_phonetic && (
                <span style={{ fontSize: 13, color: 'var(--theme-text-secondary)', fontWeight: 500 }}>
                  [{addPhoneticStress(word.russian_phonetic, word.transcription || '')}]
                </span>
              )}
              {word.cefr_level && (
                <span style={{
                  padding: '2px 6px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  color: '#3b82f6',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 700,
                  marginLeft: 'auto',
                }}>
                  {word.cefr_level}
                </span>
              )}
              {word.part_of_speech && (
                <span style={{
                  padding: '2px 6px',
                  background: 'rgba(139, 92, 246, 0.1)',
                  color: '#8b5cf6',
                  borderRadius: 4,
                  fontSize: 11,
                }}>
                  {word.part_of_speech}
                </span>
              )}
            </div>
          )}

          {/* Прогресс изучения */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Прогресс
              </span>
              <span style={{ fontSize: 11, color: 'var(--theme-text-muted)' }}>
                {Math.round(totalProgress)}%
              </span>
            </div>
            {/* Трёхсекционный прогресс-бар */}
            <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: 'var(--theme-bg)', gap: 1 }}>
              <div style={{ width: `${mcProgress}%`, background: 'var(--mode-mc)', transition: 'width 0.3s' }} />
              <div style={{ width: `${inputProgress}%`, background: 'var(--mode-input)', transition: 'width 0.3s' }} />
              <div style={{ width: `${srsProgress}%`, background: 'var(--mode-srs)', transition: 'width 0.3s' }} />
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--theme-text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <span>MC: En→Ru {word.mc_en_ru_progress}/{mcReps}, Ru→En {word.mc_ru_en_progress}/{mcReps}</span>
              {word.srs_level > 0 && (
                <span>SRS {word.srs_level}/9 · следующий: {formatNextReview(word.next_review_at)}</span>
              )}
              {(word.success_count > 0 || word.error_count > 0) && (
                <span>✓{word.success_count} ✗{word.error_count}</span>
              )}
            </div>
          </div>

          {/* Plural form */}
          {word.plural_form && (
            <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--theme-text-secondary)' }}>
              <span style={{ color: 'var(--theme-text-muted)' }}>мн.ч.: </span>
              <span>{word.plural_form}</span>
            </div>
          )}

          {(() => {
            const otherTranslations = word.translations
              .filter(t => t.translation_text !== word.main_translation_text);
            if (otherTranslations.length === 0) return null;
            return (
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Другие переводы
                </span>
                <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {otherTranslations.map(t => (
                    <span key={t.id} style={{
                      padding: '2px 8px',
                      background: 'var(--theme-bg)',
                      borderRadius: 999,
                      fontSize: 12,
                      color: 'var(--theme-text-secondary)',
                    }}>
                      {t.translation_text}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}

          {word.examples.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <span style={{ fontSize: 11, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Примеры
              </span>
              {word.examples.map(e => (
                <div key={e.id} style={{ marginTop: 6, fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                  <button
                    type="button"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      speak(e.example_sentence);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '1px 3px',
                      fontSize: 13,
                      lineHeight: 1.4,
                      color: 'var(--theme-text-muted)',
                      flexShrink: 0,
                    }}
                    title="Озвучить пример"
                  >
                    🔊
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: 'var(--theme-text)' }}>
                      {renderHighlightedSentence(e.example_sentence, word.original_text, { fontWeight: 700, color: 'var(--theme-primary)' })}
                    </div>
                    <div style={{ color: 'var(--theme-text-secondary)', marginTop: 2 }}>{e.example_translation}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Папки слова */}
          <div style={{ marginBottom: 10 }}>
            {folders.length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {(word.folder_ids ?? []).map(fid => {
                  const f = folders.find(x => x.id === fid);
                  return f ? (
                    <span
                      key={fid}
                      onClick={(e) => { e.stopPropagation(); onFolderToggle?.(word.id, fid, false); }}
                      style={{
                        padding: '3px 10px',
                        background: 'var(--theme-primary)20',
                        border: '1px solid var(--theme-primary)',
                        borderRadius: 999,
                        fontSize: 12,
                        color: 'var(--theme-primary)',
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}
                      title="Убрать из папки"
                    >
                      📁 {f.name} ×
                    </span>
                  ) : null;
                })}
                <button
                  onClick={(e) => { e.stopPropagation(); setShowFolderPicker(p => !p); }}
                  style={{
                    padding: '3px 10px',
                    background: 'var(--theme-bg)',
                    border: '1px solid var(--theme-border)',
                    borderRadius: 999,
                    fontSize: 12,
                    color: 'var(--theme-text-muted)',
                    cursor: 'pointer',
                  }}
                >
                  + в папку
                </button>
              </div>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); onOpenCreateFolder?.(); }}
                style={{
                  padding: '3px 10px',
                  background: 'transparent',
                  border: '1px dashed var(--theme-border)',
                  borderRadius: 999,
                  fontSize: 12,
                  color: 'var(--theme-text-muted)',
                  cursor: 'pointer',
                }}
              >
                ➕ Создать папку
              </button>
            )}

            {showFolderPicker && folders.length > 0 && (
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {folders
                  .filter(f => !(word.folder_ids ?? []).includes(f.id))
                  .map(f => (
                    <button
                      key={f.id}
                      onClick={(e) => { e.stopPropagation(); onFolderToggle?.(word.id, f.id, true); setShowFolderPicker(false); }}
                      style={{
                        padding: '4px 12px',
                        background: 'var(--theme-bg-elevated)',
                        border: '1px solid var(--theme-border)',
                        borderRadius: 8,
                        fontSize: 12,
                        color: 'var(--theme-text)',
                        cursor: 'pointer',
                      }}
                    >
                      📁 {f.name}
                    </button>
                  ))
                }
                <button
                  onClick={(e) => { e.stopPropagation(); setShowFolderPicker(false); onOpenCreateFolder?.(); }}
                  style={{
                    padding: '4px 10px',
                    background: 'transparent',
                    border: '1px dashed var(--theme-border)',
                    borderRadius: 8,
                    fontSize: 12,
                    color: 'var(--theme-primary)',
                    cursor: 'pointer',
                  }}
                >
                  ➕ Новая папка
                </button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(word); }}
              style={{
                flex: 1,
                padding: '8px',
                background: 'var(--theme-bg)',
                border: '1px solid var(--theme-border)',
                borderRadius: 8,
                color: 'var(--theme-text)',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              ✏️ Редактировать
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onReset(word.id); }}
              title="Сбросить в MC"
              style={{
                padding: '8px 12px',
                background: 'rgba(255, 152, 0, 0.1)',
                border: '1px solid var(--theme-warning)',
                borderRadius: 8,
                color: 'var(--theme-warning)',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              🔄
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onToggleHidden(word.id, !word.is_hidden); }}
              style={{
                padding: '8px 12px',
                background: 'var(--theme-bg)',
                border: '1px solid var(--theme-border)',
                borderRadius: 8,
                color: 'var(--theme-text-secondary)',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {word.is_hidden ? '👁️' : '🙈'}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(word.id); }}
              style={{
                padding: '8px 16px',
                background: 'rgba(255, 82, 82, 0.1)',
                border: '1px solid var(--theme-error)',
                borderRadius: 8,
                color: 'var(--theme-error)',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              🗑️
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// Согласовывает примеры с актуальным списком переводов после клика «Перевести».
// Инвариант: для каждого перевода (main + alts) есть ровно один слот примера.
// Приоритет заполнения слота: 1) существующий пример юзера с матчингом for_translation;
// 2) AI-пример с матчингом for_translation; 3) существующий «orphan»-пример без атрибуции;
// 4) AI-пример без атрибуции (positional); 5) пустой слот для ручного ввода.
// Кастомные пользовательские примеры с непустым контентом, не привязанные ни к одному
// переводу, попадают в конец списка, чтобы не затирались.
function reconcileExamples(
  finalTranslations: string[],
  existing: Array<{ sentence: string; translation: string; for_translation: string }>,
  incoming: Array<{ sentence: string; translation: string; for_translation?: string }>,
): Array<{ sentence: string; translation: string; for_translation: string }> {
  const norm = (s: string) => s.toLowerCase().trim();
  const targetKeys = finalTranslations.map(norm).filter(k => k.length > 0);
  const targetSet = new Set(targetKeys);

  const existingByKey = new Map<string, { sentence: string; translation: string; for_translation: string }>();
  const existingOrphans: Array<{ sentence: string; translation: string; for_translation: string }> = [];
  for (const ex of existing) {
    const k = norm(ex.for_translation);
    if (k && targetSet.has(k)) {
      // Пример привязан к актуальному переводу — кладём в слот (только первое вхождение).
      if (!existingByKey.has(k)) existingByKey.set(k, ex);
      // Дубликаты с тем же for_translation отбрасываются, чтобы не плодить.
    } else if (k) {
      // for_translation непустой, но НЕ совпадает ни с одним актуальным переводом —
      // значит юзер удалил/изменил этот перевод. Пример тоже выбрасываем — соответствие
      // «один перевод ↔ один пример» поддерживается, при возврате перевода AI сгенерит заново.
    } else if (ex.sentence.trim() || ex.translation.trim()) {
      // for_translation пустой, но контент есть — это кастомный пример (рукой добавленный
      // через «+ Добавить пример» или загруженный из БД старого слова). Сохраняем как orphan.
      existingOrphans.push(ex);
    }
  }

  const incomingByKey = new Map<string, { sentence: string; translation: string; for_translation?: string }>();
  const incomingPositional: Array<{ sentence: string; translation: string; for_translation?: string }> = [];
  for (const ex of incoming) {
    const k = norm(ex.for_translation ?? '');
    if (k && targetSet.has(k) && !incomingByKey.has(k)) {
      incomingByKey.set(k, ex);
    } else {
      incomingPositional.push(ex);
    }
  }

  const slots: Array<{ sentence: string; translation: string; for_translation: string }> = [];
  for (const t of finalTranslations) {
    const k = norm(t);
    let chosen = existingByKey.get(k) ?? incomingByKey.get(k);
    if (!chosen && existingOrphans.length > 0) {
      chosen = existingOrphans.shift();
    }
    if (!chosen && incomingPositional.length > 0) {
      chosen = incomingPositional.shift();
    }
    if (chosen) {
      slots.push({
        sentence: chosen.sentence,
        translation: chosen.translation,
        for_translation: t,
      });
    } else {
      slots.push({ sentence: '', translation: '', for_translation: t });
    }
  }

  // Кастомные пользовательские примеры (не привязанные ни к одному переводу) — в конец
  for (const ex of existingOrphans) {
    slots.push({
      sentence: ex.sentence,
      translation: ex.translation,
      for_translation: ex.for_translation || '',
    });
  }
  return slots;
}

interface AddWordForm {
  original_text: string;
  main_translation_text: string;
  additional_translations: string;
  is_phrase: boolean;
  plural_form: string;
  part_of_speech: string;
  cefr_level: string;
  transcription: string;
  russian_phonetic: string;
  examples: Array<{ sentence: string; translation: string; for_translation: string }>;
}

function AddWordModal({ onClose, onSaved, editWord, isDesktop = false, folderId, folderName, pendingDeletionIds }: {
  onClose: () => void;
  onSaved: () => void;
  editWord?: Word | null;
  isDesktop?: boolean;
  folderId?: number | null;
  folderName?: string | null;
  pendingDeletionIds?: Set<number>;
}) {
  const [form, setForm] = useState<AddWordForm>({
    original_text: editWord?.original_text || '',
    main_translation_text: editWord?.main_translation_text || '',
    additional_translations: editWord?.translations.map(t => t.translation_text).join(', ') || '',
    is_phrase: (editWord as any)?.is_phrase || false,
    plural_form: editWord?.plural_form || '',
    part_of_speech: (editWord as any)?.part_of_speech || '',
    cefr_level: (editWord as any)?.cefr_level || '',
    transcription: (editWord as any)?.transcription || '',
    russian_phonetic: (editWord as any)?.russian_phonetic || '',
    examples: editWord?.examples?.map(ex => ({ sentence: ex.example_sentence, translation: ex.example_translation, for_translation: '' })) || [],
  });
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateWord, setDuplicateWord] = useState<Word | null>(null);
  const [detectedDirection, setDetectedDirection] = useState<'en' | 'ru' | null>(null);
  const [translationSource, setTranslationSource] = useState<'dictionary' | 'ai' | null>(null);
  const [moreNotice, setMoreNotice] = useState<string | null>(null);
  const [correctedSuggestion, setCorrectedSuggestion] = useState<string | null>(null);
  // Текст, для которого уже сделан AI-перевод. Если совпадает с form.original_text —
  // показываем кнопку «Ещё варианты перевода», чтобы пользователь видел что новые
  // варианты добавятся к существующим, а не затрут их.
  const [lastTranslatedText, setLastTranslatedText] = useState<string>(editWord?.original_text || '');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editWord?.id) return;

    let timer: any;
    let pollCount = 0;

    const checkFresh = async () => {
      try {
        const fresh = await api.get<Word>(`/words/${editWord.id}`);
        if (fresh && fresh.examples && fresh.examples.length > form.examples.length) {
          setForm(f => ({
            ...f,
            examples: fresh.examples.map(ex => ({
              sentence: ex.example_sentence,
              translation: ex.example_translation,
              for_translation: '',
            })),
            transcription: f.transcription || (fresh as any).transcription || '',
            russian_phonetic: f.russian_phonetic || (fresh as any).russian_phonetic || '',
            part_of_speech: f.part_of_speech || (fresh as any).part_of_speech || '',
            cefr_level: f.cefr_level || (fresh as any).cefr_level || '',
          }));
          return;
        }
      } catch {}

      pollCount++;
      if (pollCount < 4) {
        timer = setTimeout(checkFresh, 1800);
      }
    };

    checkFresh();

    return () => clearTimeout(timer);
  }, [editWord?.id]);

  // Проверяем дубликат по exact match
  const checkDuplicate = async (text: string, checkField: 'original' | 'translation' = 'original'): Promise<Word | null> => {
    if (!text.trim()) return null;
    try {
      const result = await api.get<{ words: Word[] }>(`/words?search=${encodeURIComponent(text.trim())}&limit=5`);
      const match = result.words.find(w => {
        // Исключаем слова с отложенным удалением
        if (pendingDeletionIds?.has(w.id)) return false;
        if (checkField === 'translation') {
          return w.main_translation_text.toLowerCase() === text.trim().toLowerCase();
        }
        return w.original_text.toLowerCase() === text.trim().toLowerCase();
      });
      return match || null;
    } catch {
      return null;
    }
  };

  const handleBlurOriginal = async () => {
    if (!form.original_text.trim() || editWord) return;
    const isCyrillic = /[а-яёА-ЯЁ]/.test(form.original_text);
    const dup = isCyrillic
      ? await checkDuplicate(form.original_text, 'translation')
      : await checkDuplicate(form.original_text, 'original');
    setDuplicateWord(dup);
  };

  const handleTranslate = async (textOverride?: string, isCorrection?: boolean) => {
    const rawInput = typeof textOverride === 'string' ? textOverride : form.original_text;
    if (!rawInput.trim()) return;
    setTranslating(true);
    setError(null);
    setDuplicateWord(null);
    setTranslationSource(null);

    const inputText = rawInput.trim();
    const isRuInput = /[а-яёА-ЯЁ]/.test(inputText);

    const isDifferentWord = !lastTranslatedText || lastTranslatedText.toLowerCase() !== inputText.toLowerCase();
    const shouldResetExisting = Boolean(isCorrection || isDifferentWord);

    const isMoreRequest = Boolean(
      !shouldResetExisting &&
      lastTranslatedText &&
      lastTranslatedText.toLowerCase() === inputText.toLowerCase() &&
      form.main_translation_text.trim()
    );

    const currentAltList = shouldResetExisting
      ? []
      : form.additional_translations.split(',').map(t => t.trim()).filter(Boolean);

    const existingTranslations = shouldResetExisting
      ? []
      : [form.main_translation_text.trim(), ...currentAltList].filter(Boolean);

    // Для ru→en дубликат проверяем по переводу после перевода, для en→ru — сразу
    // При редактировании или запросе «ещё варианты» — пропускаем проверку
    if (!isRuInput && !editWord && !isMoreRequest) {
      const dup = await checkDuplicate(inputText, 'original');
      if (dup) {
        setDuplicateWord(dup);
        setTranslating(false);
        return;
      }
    }

    try {
      const result = await api.post<{
        main_translation: string;
        alternative_translations: string[];
        plural_form?: string | null;
        source_language?: string;
        part_of_speech?: string | null;
        cefr_level?: string | null;
        transcription?: string | null;
        russian_phonetic?: string | null;
        corrected_text?: string | null;
        examples?: Array<{ sentence: string; translation: string; for_translation?: string }>;
        _source?: 'dictionary' | 'ai';
      }>(
        '/ai/translate',
        {
          text: inputText,
          more: isMoreRequest,
          existing_translations: isMoreRequest ? existingTranslations : undefined,
        }
      );

      if (result.corrected_text && result.corrected_text.toLowerCase() !== inputText.toLowerCase()) {
        setCorrectedSuggestion(result.corrected_text);
      } else {
        setCorrectedSuggestion(null);
      }

      if (isMoreRequest) {
        const newAlts = result.alternative_translations || [];
        const existingNorm = new Set(existingTranslations.map(t => t.toLowerCase().trim()));
        const toAdd = newAlts.filter(t => !existingNorm.has(t.toLowerCase().trim()));

        if (toAdd.length > 0) {
          const updatedAlts = [...currentAltList, ...toAdd].join(', ');
          setForm(f => ({
            ...f,
            additional_translations: updatedAlts,
          }));
          setMoreNotice(`+ Добавлено: ${toAdd.join(', ')}`);
          setTimeout(() => setMoreNotice(null), 4000);
        } else {
          setMoreNotice('Все основные варианты уже добавлены');
          setTimeout(() => setMoreNotice(null), 3000);
        }
        setTranslating(false);
        return;
      }

      if (isRuInput) {
        // ru→en: свопаем поля
        const englishWord = result.main_translation;
        const autoPhrase = englishWord.trim().split(/\s+/).filter(Boolean).length > 2;

        // Проверяем дубликат по полученному английскому слову (не при редактировании)
        if (!editWord) {
          const dup = await checkDuplicate(englishWord, 'original');
          if (dup) {
            setDuplicateWord(dup);
            setTranslating(false);
            return;
          }
        }

        setDetectedDirection('ru');
        setForm(f => ({
          ...f,
          original_text: englishWord,
          main_translation_text: inputText,
          additional_translations: result.alternative_translations.join(', '),
          plural_form: result.plural_form || '',
          is_phrase: autoPhrase,
          part_of_speech: result.part_of_speech || '',
          cefr_level: result.cefr_level || '',
          transcription: result.transcription || '',
          russian_phonetic: result.russian_phonetic || '',
          examples: reconcileExamples(
            [inputText, ...result.alternative_translations],
            shouldResetExisting ? [] : f.examples,
            result.examples ?? [],
          ),
        }));
        setTranslationSource(result._source ?? null);
        setLastTranslatedText(englishWord);
      } else {
        // en→ru:
        setDetectedDirection('en');
        const autoPhrase = inputText.trim().split(/\s+/).filter(Boolean).length > 2;
        const newMain = (shouldResetExisting ? null : form.main_translation_text) || result.main_translation;
        const existingAlt = shouldResetExisting
          ? []
          : form.additional_translations.split(',').map(t => t.trim()).filter(Boolean);
        const newTranslations = [result.main_translation, ...result.alternative_translations];
        const existingNorm = new Set([newMain, ...existingAlt].map(t => t.toLowerCase().trim()));
        const toAdd = newTranslations.filter(t => !existingNorm.has(t.toLowerCase().trim()));
        const mergedAlt = shouldResetExisting
          ? result.alternative_translations.join(', ')
          : [...existingAlt, ...toAdd].filter(Boolean).join(', ');

        setForm(f => ({
          ...f,
          original_text: inputText,
          main_translation_text: newMain,
          additional_translations: mergedAlt,
          is_phrase: autoPhrase,
          plural_form: (shouldResetExisting ? null : f.plural_form) || result.plural_form || '',
          part_of_speech: (shouldResetExisting ? null : f.part_of_speech) || result.part_of_speech || '',
          cefr_level: (shouldResetExisting ? null : f.cefr_level) || result.cefr_level || '',
          transcription: (shouldResetExisting ? null : f.transcription) || result.transcription || '',
          russian_phonetic: (shouldResetExisting ? null : f.russian_phonetic) || result.russian_phonetic || '',
          examples: reconcileExamples(
            [newMain, ...mergedAlt.split(',').map(t => t.trim()).filter(Boolean)],
            shouldResetExisting ? [] : f.examples,
            result.examples ?? [],
          ),
        }));
        setTranslationSource(result._source ?? null);
        setLastTranslatedText(inputText);
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка при переводе');
    } finally {
      setTranslating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (duplicateWord) return;
    setLoading(true);
    setError(null);

    const additionalTranslations = form.additional_translations
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);

    try {
      if (editWord) {
        await api.put(`/words/${editWord.id}`, {
          original_text: form.original_text,
          main_translation_text: form.main_translation_text,
          additional_translations: additionalTranslations,
          is_phrase: form.is_phrase,
          plural_form: form.plural_form || null,
          examples: form.examples.length > 0
            ? form.examples
                .filter(ex => ex.sentence.trim() && ex.translation.trim())
                .map(ex => ({ sentence: ex.sentence, translation: ex.translation }))
            : [],
        });
        trackEvent('word_edited', { is_phrase: form.is_phrase });
      } else {
        const newWord = await api.post<{ id: number }>('/words', {
          original_text: form.original_text,
          main_translation_text: form.main_translation_text,
          additional_translations: additionalTranslations,
          is_phrase: form.is_phrase,
          plural_form: form.plural_form || null,
          part_of_speech: form.part_of_speech || null,
          cefr_level: form.cefr_level || null,
          transcription: form.transcription || null,
          russian_phonetic: form.russian_phonetic || null,
          examples: form.examples.length > 0
            ? form.examples
                .filter(ex => ex.sentence.trim() && ex.translation.trim())
                .map(ex => ({ sentence: ex.sentence, translation: ex.translation }))
            : undefined,
        });
        // Автоматически добавляем слово в текущую открытую папку
        if (folderId && newWord?.id) {
          await api.post(`/folders/${folderId}/words/${newWord.id}`, {});
        }
        trackEvent('word_added', { is_phrase: form.is_phrase });
      }
      onSaved();
      if (editWord) {
        // При редактировании закрываем как обычно
        onClose();
      } else {
        // При добавлении — сбрасываем форму, остаёмся открытыми для быстрого добавления следующего
        setForm({ original_text: '', main_translation_text: '', additional_translations: '', is_phrase: false, plural_form: '', part_of_speech: '', cefr_level: '', transcription: '', russian_phonetic: '', examples: [] });
        setDuplicateWord(null);
        setDetectedDirection(null);
        setTranslationSource(null);
        setLastTranslatedText('');
        scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка при сохранении');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--theme-bg-overlay)',
        zIndex: 1000,
        display: 'flex',
        alignItems: isDesktop ? 'center' : 'flex-end',
        justifyContent: isDesktop ? 'center' : undefined,
        padding: isDesktop ? 24 : 0,
      }}>
      <div
        ref={scrollContainerRef}
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: isDesktop ? 480 : undefined,
          background: 'var(--theme-bg-elevated)',
          borderRadius: isDesktop ? 20 : '20px 20px 0 0',
          maxHeight: '90vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
        {/* Sticky шапка — всегда видна даже когда клавиатура открыта */}
        <div style={{
          position: 'sticky',
          top: 0,
          background: 'var(--theme-bg-elevated)',
          borderRadius: '20px 20px 0 0',
          padding: '24px 16px 0',
          zIndex: 1,
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: folderId && !editWord ? 8 : 20 }}>
          <h2 style={{ margin: 0, color: 'var(--theme-text)', fontSize: 18 }}>
            {editWord ? 'Редактировать слово' : 'Добавить слово'}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--theme-text-secondary)', fontSize: 24, cursor: 'pointer', padding: 4 }}
          >
            ✕
          </button>
        </div>
        {folderId && !editWord && folderName && (
          <div style={{ fontSize: 12, color: 'var(--theme-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>📁</span>
            <span>Слово попадёт в папку «{folderName}»</span>
          </div>
        )}
        </div>{/* конец sticky шапки */}

        <form onSubmit={handleSubmit} style={{ padding: '0 16px', paddingBottom: 'calc(24px + var(--safe-area-bottom))' }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}>
              {form.is_phrase ? 'Фраза или перевод' : 'Слово или перевод'}
            </label>
            <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={form.original_text}
              onChange={e => {
                const val = e.target.value;
                // Auto-detect фраза: >2 слов → is_phrase=true
                const autoPhrase = val.trim().split(/\s+/).filter(Boolean).length > 2;
                const hasTranslation = !!form.main_translation_text;
                if (duplicateWord || hasTranslation) {
                  // Сбрасываем нижние поля при изменении верхнего (перевод устарел)
                  setForm({ original_text: val, main_translation_text: '', additional_translations: '', is_phrase: autoPhrase, plural_form: '', part_of_speech: '', cefr_level: '', transcription: '', russian_phonetic: '', examples: [] });
                  setTranslationSource(null);
                } else {
                  setForm({ ...form, original_text: val, is_phrase: autoPhrase });
                }
                setDetectedDirection(null);
                setDuplicateWord(null);
                setCorrectedSuggestion(null);
              }}
              onBlur={handleBlurOriginal}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleTranslate(); } }}
              placeholder={form.is_phrase ? 'например: take care of или заботиться о' : 'например: abandon или отказаться'}
              required
              style={{
                width: '100%',
                padding: '12px',
                paddingRight: 36,
                background: 'var(--theme-bg)',
                border: '1px solid var(--theme-border)',
                borderRadius: 8,
                color: 'var(--theme-text)',
                fontSize: 16,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {form.original_text && (
              <button
                type="button"
                onClick={() => {
                  setForm({ original_text: '', main_translation_text: '', additional_translations: '', is_phrase: false, plural_form: '', part_of_speech: '', cefr_level: '', transcription: '', russian_phonetic: '', examples: [] });
                  setDuplicateWord(null);
                  setDetectedDirection(null);
                  setCorrectedSuggestion(null);
                }}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--theme-text-muted)',
                  fontSize: 18,
                  cursor: 'pointer',
                  padding: '4px 6px',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            )}
            </div>

            {correctedSuggestion && (
              <div style={{
                marginTop: 8,
                padding: '8px 12px',
                background: 'rgba(234, 179, 8, 0.12)',
                border: '1px solid rgba(234, 179, 8, 0.35)',
                borderRadius: 8,
                fontSize: 13,
                color: 'var(--theme-text)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}>
                <div>
                  💡 Возможно: <strong>{correctedSuggestion}</strong>?
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const fixed = correctedSuggestion;
                    setCorrectedSuggestion(null);
                    setForm(f => ({
                      ...f,
                      original_text: fixed,
                      main_translation_text: '',
                      additional_translations: '',
                      examples: [],
                    }));
                    handleTranslate(fixed, true);
                  }}
                  style={{
                    background: '#eab308',
                    color: '#000',
                    border: 'none',
                    borderRadius: 6,
                    padding: '4px 10px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Исправить
                </button>
              </div>
            )}
          </div>

          <div style={{ marginBottom: detectedDirection === 'ru' ? 8 : 12 }}>
            <button
              type="button"
              onClick={() => handleTranslate()}
              disabled={!form.original_text.trim() || translating}
              style={{
                width: '100%',
                padding: '10px',
                background: (!form.original_text.trim() || translating) ? 'var(--theme-bg)' : 'rgba(var(--theme-primary-rgb, 79, 70, 229), 0.1)',
                border: '1px solid var(--theme-border)',
                borderRadius: 8,
                color: (!form.original_text.trim() || translating) ? 'var(--theme-text-muted)' : 'var(--theme-primary)',
                fontSize: 14,
                cursor: (!form.original_text.trim() || translating) ? 'not-allowed' : 'pointer',
                fontWeight: 500,
              }}
            >
              {translating
                ? '⏳ Перевожу...'
                : (lastTranslatedText && lastTranslatedText === form.original_text.trim()
                    ? '✨ Ещё варианты перевода'
                    : '✨ Автоматически перевести')}
            </button>
            {translationSource && (
              <div style={{
                fontSize: 11,
                color: translationSource === 'dictionary' ? '#4caf50' : 'var(--theme-text-secondary)',
                textAlign: 'center',
                marginTop: 4,
                fontWeight: translationSource === 'dictionary' ? 600 : 400,
              }}>
                {translationSource === 'dictionary' ? '⚡ Из словаря' : '🤖 AI перевод'}
              </div>
            )}
            {moreNotice && (
              <div style={{
                fontSize: 12,
                color: moreNotice.startsWith('+') ? '#4caf50' : 'var(--theme-text-muted)',
                textAlign: 'center',
                marginTop: 6,
                fontWeight: 500,
              }}>
                {moreNotice.startsWith('+') ? '✨ ' : '💡 '}{moreNotice}
              </div>
            )}
          </div>

          {detectedDirection === 'ru' && (
            <div style={{
              marginBottom: 12,
              padding: '6px 10px',
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: 8,
              fontSize: 12,
              color: 'var(--theme-text-secondary)',
            }}>
              🔄 Определён русский → переведено на английский
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}>
              {form.is_phrase ? 'Перевод фразы (русский)' : 'Перевод (русский)'}
            </label>
            <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={form.main_translation_text}
              onChange={e => setForm({ ...form, main_translation_text: e.target.value })}
              placeholder={form.is_phrase ? 'например: заботиться о' : 'например: бросать, покидать'}
              required
              style={{
                width: '100%',
                padding: '12px',
                paddingRight: 36,
                background: 'var(--theme-bg)',
                border: '1px solid var(--theme-border)',
                borderRadius: 8,
                color: 'var(--theme-text)',
                fontSize: 16,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {form.main_translation_text && (
              <button
                type="button"
                onClick={() => setForm({ ...form, main_translation_text: '' })}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--theme-text-muted)',
                  fontSize: 18,
                  cursor: 'pointer',
                  padding: '4px 6px',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            )}
            </div>
          </div>

          {/* Множественное число (plural_form) — показываем если не фраза */}
          {!form.is_phrase && form.plural_form && (
            <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(var(--theme-primary-rgb, 79, 70, 229), 0.08)', borderRadius: 8, fontSize: 12, color: 'var(--theme-text-secondary)' }}>
              Мн.ч.: <strong>{form.plural_form}</strong>
            </div>
          )}

          {/* Auto-detect: тип определяется автоматически по количеству слов */}
          {form.is_phrase && (
            <div style={{ marginBottom: 12, padding: '6px 10px', background: 'rgba(var(--theme-primary-rgb, 79, 70, 229), 0.08)', borderRadius: 8, fontSize: 12, color: 'var(--theme-text-secondary)' }}>
              💬 Автоопределено как фраза
            </div>
          )}

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}>
              Дополнительные переводы (через запятую, необязательно)
            </label>
            <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={form.additional_translations}
              onChange={e => setForm({ ...form, additional_translations: e.target.value })}
              placeholder="например: оставлять, отказываться"
              style={{
                width: '100%',
                padding: '12px',
                paddingRight: 36,
                background: 'var(--theme-bg)',
                border: '1px solid var(--theme-border)',
                borderRadius: 8,
                color: 'var(--theme-text)',
                fontSize: 16,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {form.additional_translations && (
              <button
                type="button"
                onClick={() => setForm({ ...form, additional_translations: '' })}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--theme-text-muted)',
                  fontSize: 18,
                  cursor: 'pointer',
                  padding: '4px 6px',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            )}
            </div>
          </div>

          {/* Примеры (список) */}
          {(form.examples.length > 0 || form.main_translation_text) && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}>
                Примеры (необязательно)
                {form.examples.length > 0 && <span style={{ marginLeft: 6, color: 'var(--theme-text-muted)' }}>· {form.examples.length}</span>}
              </label>
              {form.examples.map((ex, i) => (
                <div
                  key={i}
                  style={{
                    position: 'relative',
                    marginBottom: 8,
                    padding: '8px 32px 8px 8px',
                    border: '1px solid var(--theme-border)',
                    borderRadius: 8,
                    background: 'var(--theme-bg)',
                  }}
                >
                  {ex.for_translation && (
                    <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', marginBottom: 6 }}>
                      для значения «{ex.for_translation}»
                    </div>
                  )}
                  <input
                    type="text"
                    value={ex.sentence}
                    onChange={e => {
                      const next = form.examples.slice();
                      next[i] = { ...next[i], sentence: e.target.value };
                      setForm({ ...form, examples: next });
                    }}
                    placeholder="Пример на английском..."
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      background: 'var(--theme-bg-secondary, var(--theme-bg))',
                      border: '1px solid var(--theme-border)',
                      borderRadius: 6,
                      color: 'var(--theme-text)',
                      fontSize: 14,
                      outline: 'none',
                      boxSizing: 'border-box',
                      marginBottom: 6,
                    }}
                  />
                  <input
                    type="text"
                    value={ex.translation}
                    onChange={e => {
                      const next = form.examples.slice();
                      next[i] = { ...next[i], translation: e.target.value };
                      setForm({ ...form, examples: next });
                    }}
                    placeholder="Перевод примера..."
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      background: 'var(--theme-bg-secondary, var(--theme-bg))',
                      border: '1px solid var(--theme-border)',
                      borderRadius: 6,
                      color: 'var(--theme-text-secondary)',
                      fontSize: 14,
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = form.examples.slice();
                      next.splice(i, 1);
                      setForm({ ...form, examples: next });
                    }}
                    aria-label="Удалить пример"
                    title="Удалить пример"
                    style={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      width: 22,
                      height: 22,
                      lineHeight: '20px',
                      padding: 0,
                      background: 'transparent',
                      border: 'none',
                      borderRadius: 4,
                      color: 'var(--theme-text-muted)',
                      fontSize: 18,
                      cursor: 'pointer',
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setForm({ ...form, examples: [...form.examples, { sentence: '', translation: '', for_translation: '' }] })}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  background: 'transparent',
                  border: '1px dashed var(--theme-border)',
                  borderRadius: 8,
                  color: 'var(--theme-text-secondary)',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                + Добавить пример
              </button>
            </div>
          )}

          {/* Блок дубликата */}
          {duplicateWord && (
            <div style={{
              padding: '12px',
              background: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid var(--theme-warning)',
              borderRadius: 8,
              marginBottom: 12,
            }}>
              <div style={{ fontSize: 13, color: 'var(--theme-warning)', fontWeight: 600, marginBottom: 6 }}>
                ⚠️ Слово уже в словаре
              </div>
              <div style={{ fontSize: 13, color: 'var(--theme-text)' }}>
                <strong>{duplicateWord.original_text}</strong> — {duplicateWord.main_translation_text}
              </div>
              {duplicateWord.translations.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginTop: 2 }}>
                  Также: {duplicateWord.translations.map(t => t.translation_text).join(', ')}
                </div>
              )}
              {duplicateWord.plural_form && (
                <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginTop: 2 }}>
                  Мн.ч.: {duplicateWord.plural_form}
                </div>
              )}
            </div>
          )}

          {error && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(255, 82, 82, 0.1)',
              border: '1px solid var(--theme-error)',
              borderRadius: 8,
              color: 'var(--theme-error)',
              fontSize: 13,
              marginBottom: 12,
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || (!!duplicateWord && !editWord)}
            style={{
              width: '100%',
              padding: '14px',
              background: (loading || (duplicateWord && !editWord)) ? 'var(--theme-border)' : 'var(--theme-primary)',
              color: 'var(--theme-primary-text)',
              border: 'none',
              borderRadius: 10,
              fontSize: 16,
              fontWeight: 600,
              cursor: (loading || (duplicateWord && !editWord)) ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Сохранение...' : (editWord ? 'Сохранить' : (form.is_phrase ? 'Добавить фразу' : 'Добавить слово'))}
          </button>
        </form>
      </div>
    </div>
  );
}

export function WordsPage() {
  const isDesktop = useIsDesktop();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const renamingRef = useRef(false);
  const [wordsData, setWordsData] = useState<WordsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const requestIdRef = useRef(0);
  const [showModal, setShowModal] = useState(false);
  const [editWord, setEditWord] = useState<Word | null>(null);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [pendingDeletions, setPendingDeletions] = useState<Map<number, PendingDeletion>>(new Map());
  const [addingTop100, setAddingTop100] = useState(false);
  // Папки
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [showFolderManager, setShowFolderManager] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  // Inline переименование папки
  const [editingFolderId, setEditingFolderId] = useState<number | null>(null);

  const isTabActive = useIsTabActive();

  // Фоновая тихая валидация при переходе на вкладку "Слова"
  useOnTabActive(() => {
    loadWords();
    loadFolders();
  });

  // Если открыто модальное окно добавления слова, редактирования или папок — закрываем его
  useBackHandler(() => {
    if (showModal) {
      setShowModal(false);
    } else if (showFolderManager) {
      setShowFolderManager(false);
    } else if (editWord) {
      setEditWord(null);
    }
  }, isTabActive && Boolean(showModal || showFolderManager || editWord), 5);
  const [editingFolderName, setEditingFolderName] = useState('');
  // Dropdown папок
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [createFolderName, setCreateFolderName] = useState('');
  // Диалог удаления папки
  const [deletingFolder, setDeletingFolder] = useState<Folder | null>(null);
  // Открытая карточка (только одна)
  const [expandedWordId, setExpandedWordId] = useState<number | null>(null);
  // Бесконечная прокрутка (серверная пагинация)
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Массовое выделение
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkFolderPicker, setBulkFolderPicker] = useState(false);
  // mcReps для прогресс-бара (по умолчанию 1)
  const mcReps = 1;

  // Search input ref (для фокусировки и восстановления клавиатуры)
  const searchInputRef = useRef<HTMLInputElement>(null);

  // In-app модалки (вместо native confirm/alert, ломающих фокус в Telegram Desktop)
  const [showTop100Modal, setShowTop100Modal] = useState(false);
  const [resettingWord, setResettingWord] = useState<Word | null>(null);
  const [showBulkResetModal, setShowBulkResetModal] = useState(false);
  const [errorToast, setErrorToast] = useState<string | null>(null);

  const showErrorToast = (msg: string) => {
    setErrorToast(msg);
    setTimeout(() => setErrorToast(null), 4000);
  };

  const loadFolders = useCallback(async () => {
    try {
      const data = await api.get<Folder[]>('/folders');
      setFolders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Ошибка загрузки папок:', err);
    }
  }, []);

  const PAGE_SIZE = 50;

  const buildParams = useCallback((offset: number) => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset), sort, order });
    if (searchQuery) params.set('search', searchQuery);
    if (filterMode !== 'all') {
      params.set('mode', filterMode);
    } else {
      params.set('show_hidden', 'false');
    }
    if (selectedFolderId) params.set('folder_id', String(selectedFolderId));
    return params;
  }, [searchQuery, filterMode, sort, order, selectedFolderId]);

  const loadWords = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    try {
      const params = buildParams(0);
      const data = await api.get<WordsResponse>(`/words?${params}`);
      // Игнорируем если пришёл более свежий запрос
      if (requestId !== requestIdRef.current) return;
      setWordsData(data);
      setHasMore(data.words.length >= PAGE_SIZE);
    } catch (err) {
      console.error('Ошибка загрузки слов:', err);
      captureError(err, { context: 'WordsPage.loadWords' });
    } finally {
      // Сбрасываем loading только для актуального запроса
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [buildParams]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore || !wordsData) return;
    setLoadingMore(true);
    try {
      const params = buildParams(wordsData.words.length);
      const data = await api.get<WordsResponse>(`/words?${params}`);
      setWordsData(prev => prev ? {
        ...prev,
        words: [...prev.words, ...data.words],
      } : data);
      setHasMore(data.words.length >= PAGE_SIZE);
    } catch (err) {
      captureError(err, { context: 'WordsPage.loadMore' });
    } finally {
      setLoadingMore(false);
    }
  }, [buildParams, wordsData, loading, loadingMore, hasMore]);

  useEffect(() => {
    loadWords();
  }, [loadWords]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  // Debounce: мгновенно очищаем экран при печати, но запрос на сервер — через 350мс
  useEffect(() => {
    if (search === searchQuery) return;
    // Мгновенно очищаем список при любом изменении поиска, чтобы не висели старые слова
    setWordsData(null);
    setLoading(true);
    setHasMore(true);
    setExpandedWordId(null);
    const timer = setTimeout(() => {
      setSearchQuery(search);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Автооткрытие модалки добавления слова из URL (?add=true)
  useEffect(() => {
    if (searchParams.get('add') === 'true') {
      setEditWord(null);
      setShowModal(true);
      // Убираем параметр из URL чтобы не переоткрывалось при обновлении
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('add');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Сброс при смене фильтров/сортировки (не поиска — поиск handled выше)
  useEffect(() => {
    setExpandedWordId(null);
    setWordsData(null);
    setLoading(true);
    setHasMore(true);
    // loadWords сам инкрементирует requestIdRef при вызове — тут не нужно
  }, [filterMode, sort, order, selectedFolderId]);

  // IntersectionObserver для бесконечной прокрутки
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loading) loadMore();
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, loading]);

  const handleDelete = (id: number) => {
    // Удаляем из отображения сразу (optimistic)
    setWordsData(prev => prev ? {
      ...prev,
      words: prev.words.filter(w => w.id !== id),
      total: prev.total - 1,
    } : prev);

    const word = wordsData?.words.find(w => w.id === id);
    if (!word) return;

    if (word.folder_ids?.length) {
      setFolders(prev => prev.map(f => word.folder_ids?.includes(f.id) ? { ...f, word_count: Math.max(0, f.word_count - 1) } : f));
    }

    // Откладываем реальное удаление на 10 секунд (undo window)
    const timer = setTimeout(async () => {
      try {
        await api.delete(`/words/${id}`);
        trackEvent('word_deleted');
        loadFolders();
      } catch (err: any) {
        captureError(err, { context: 'WordsPage.handleDelete' });
        // Восстанавливаем слово при ошибке
        loadWords();
        loadFolders();
      }
      setPendingDeletions(prev => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    }, 10000);

    setPendingDeletions(prev => {
      const next = new Map(prev);
      next.set(id, { id, word, timer });
      return next;
    });
  };

  const handleUndoDelete = (id: number) => {
    const pending = pendingDeletions.get(id);
    if (!pending) return;
    clearTimeout(pending.timer);
    setPendingDeletions(prev => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    // Возвращаем слово в список
    setWordsData(prev => prev ? {
      ...prev,
      words: [pending.word, ...prev.words],
      total: prev.total + 1,
    } : prev);
    if (pending.word.folder_ids?.length) {
      setFolders(prev => prev.map(f => pending.word.folder_ids?.includes(f.id) ? { ...f, word_count: f.word_count + 1 } : f));
    }
  };

  const handleToggleHidden = async (id: number, is_hidden: boolean) => {
    try {
      await api.put(`/words/${id}`, { is_hidden });
      loadWords();
    } catch (err: any) {
      showErrorToast(err.message || 'Ошибка');
    }
  };

  const handleReset = (id: number) => {
    const word = wordsData?.words.find(w => w.id === id);
    if (word) {
      setResettingWord(word);
    }
  };

  const executeResetWord = async (id: number) => {
    try {
      await api.post(`/words/${id}/reset`, {});
      trackEvent('word_reset', { word_id: id });
      loadWords();
    } catch (err: any) {
      showErrorToast(err.message || 'Ошибка сброса прогресса');
    } finally {
      setResettingWord(null);
    }
  };

  const handleBulkReset = () => {
    if (selectedIds.size === 0) return;
    setShowBulkResetModal(true);
  };

  const executeBulkReset = async () => {
    if (selectedIds.size === 0) return;
    try {
      await api.post('/words/batch-reset', { ids: [...selectedIds] });
      trackEvent('words_bulk_reset', { count: selectedIds.size });
      setSelectedIds(new Set());
      setSelectionMode(false);
      loadWords();
    } catch (err: any) {
      showErrorToast(err.message || 'Ошибка сброса прогресса');
    } finally {
      setShowBulkResetModal(false);
    }
  };

  const ensureTop100Folder = async (): Promise<number | null> => {
    const FOLDER_NAME = '📚 Top 100 (A1)';
    try {
      // Проверяем среди загруженных папок
      const existing = folders.find((f: Folder) => f.name === FOLDER_NAME);
      if (existing) return existing.id;
      // Загружаем свежий список
      const freshFolders = await api.get<Array<{ id: number; name: string }>>('/folders');
      const list = Array.isArray(freshFolders) ? freshFolders : [];
      const fromList = list.find((f: any) => f.name === FOLDER_NAME);
      if (fromList) return fromList.id;
      // Создаём новую
      const created = await api.post<{ id: number }>('/folders', { name: FOLDER_NAME });
      return created?.id ?? null;
    } catch (err: any) {
      captureError(err, { context: 'WordsPage.ensureTop100Folder' });
      return null;
    }
  };

  const handleAddTop100 = () => {
    setShowTop100Modal(true);
  };

  const executeAddTop100 = async () => {
    setAddingTop100(true);
    try {
      const coursesData = await api.get<Array<{ id: number; slug: string }>>('/courses');
      const courses = Array.isArray(coursesData) ? coursesData : [];
      const top100 = courses.find((c: any) => c.slug === 'top100');
      if (!top100) {
        showErrorToast('Курс Top 100 не найден');
        return;
      }
      const folderId = await ensureTop100Folder();
      // Добавляем 100 слов курса одним вызовом
      await api.post(`/courses/${top100.id}/add-words`, { count: 100, folderId });
      await loadFolders();
      if (folderId) {
        setSelectedFolderId(folderId);
      }
      await loadWords();
      trackEvent('top100_words_added');
    } catch (err: any) {
      captureError(err, { context: 'WordsPage.executeAddTop100' });
      showErrorToast(err.message || 'Ошибка добавления слов');
    } finally {
      setAddingTop100(false);
      setShowTop100Modal(false);
    }
  };

  const handleFolderToggle = async (wordId: number, folderId: number, adding: boolean) => {
    try {
      if (adding) {
        await api.post(`/folders/${folderId}/words/${wordId}`, {});
      } else {
        await api.delete(`/folders/${folderId}/words/${wordId}`);
      }
      // Обновляем folder_ids у слова в локальном состоянии
      setWordsData(prev => {
        if (!prev) return prev;
        // Если просматриваем именно эту папку и убираем слово из неё — скрываем карточку
        if (!adding && selectedFolderId === folderId) {
          return {
            ...prev,
            words: prev.words.filter(w => w.id !== wordId),
            total: Math.max(0, prev.total - 1),
          };
        }
        return {
          ...prev,
          words: prev.words.map(w => {
            if (w.id !== wordId) return w;
            const current = w.folder_ids ?? [];
            const newIds = adding
              ? [...current, folderId]
              : current.filter(id => id !== folderId);
            return { ...w, folder_ids: newIds };
          }),
        };
      });
      loadFolders();
    } catch (err: any) {
      console.error('Ошибка управления папкой:', err);
    }
  };

  const handleCreateFolder = async (customName?: string) => {
    const nameToUse = (customName !== undefined ? customName : newFolderName).trim();
    if (!nameToUse) return;
    setCreatingFolder(true);
    try {
      const folder = await api.post<Folder>('/folders', { name: nameToUse });
      setFolders(prev => [...prev, folder]);
      setNewFolderName('');
      setCreateFolderName('');
      setShowCreateFolderModal(false);
      setSelectedFolderId(folder.id);
    } catch (err: any) {
      showErrorToast(err.message || 'Ошибка создания папки');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleRenameFolder = async (id: number) => {
    if (renamingRef.current) return;
    const name = editingFolderName.trim();
    if (!name) {
      setEditingFolderId(null);
      return;
    }
    renamingRef.current = true;
    try {
      await api.patch(`/folders/${id}`, { name });
      setFolders(prev => prev.map(f => f.id === id ? { ...f, name } : f));
      setEditingFolderId(null);
    } catch (err: any) {
      showErrorToast(err.message || 'Ошибка переименования папки (возможно такое имя уже существует)');
    } finally {
      renamingRef.current = false;
    }
  };

  const handleDeleteFolderConfirm = async (folder: Folder, deleteWords: boolean) => {
    try {
      const url = deleteWords ? `/folders/${folder.id}?deleteWords=true` : `/folders/${folder.id}`;
      const res = await api.delete<any>(url);
      setFolders(prev => prev.filter(f => f.id !== folder.id));
      if (selectedFolderId === folder.id) {
        setSelectedFolderId(null);
      }
      loadWords(); // Всегда обновляем список слов при удалении папки
    } catch (err: any) {
      showErrorToast(err.message || 'Ошибка удаления папки');
    } finally {
      setDeletingFolder(null);
    }
  };

  const handleSelectWord = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (!wordsData) return;
    const allIds = new Set(wordsData.words.map(w => w.id));
    if (selectedIds.size === allIds.size) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(allIds);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.size) return;
    const ids = Array.from(selectedIds);
    // Оптимистично убираем из UI
    setWordsData(prev => prev ? {
      ...prev,
      words: prev.words.filter(w => !selectedIds.has(w.id)),
      total: prev.total - selectedIds.size,
    } : prev);
    setSelectedIds(new Set());
    setSelectionMode(false);
    try {
      await api.post('/words/batch-delete', { ids });
      loadFolders();
    } catch (err: any) {
      captureError(err, { context: 'WordsPage.handleBulkDelete' });
      loadWords(); // Откат при ошибке
      loadFolders();
    }
  };

  const handleBulkAddToFolder = async (folderId: number) => {
    if (!selectedIds.size) return;
    const word_ids = Array.from(selectedIds);
    setBulkFolderPicker(false);
    setSelectedIds(new Set());
    setSelectionMode(false);
    try {
      await api.post(`/folders/${folderId}/words/batch`, { word_ids });
      // Обновляем folder_ids в UI
      setWordsData(prev => prev ? {
        ...prev,
        words: prev.words.map(w => word_ids.includes(w.id) ? {
          ...w,
          folder_ids: [...new Set([...(w.folder_ids ?? []), folderId])],
        } : w),
      } : prev);
      loadFolders();
    } catch (err: any) {
      captureError(err, { context: 'WordsPage.handleBulkAddToFolder' });
    }
  };

  const handleEdit = (word: Word) => {
    setEditWord(word);
    setShowModal(true);
  };

  const toggleSort = (field: string) => {
    if (sort === field) {
      setOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(field);
      setOrder('desc');
    }
  };

  const filterChips: Array<{ key: FilterMode; label: string; color: string }> = [
    { key: 'all', label: 'Все', color: 'var(--theme-text-secondary)' },
    { key: 'mc', label: 'MC', color: 'var(--mode-mc)' },
    { key: 'input', label: 'Ввод', color: 'var(--mode-input)' },
    { key: 'srs', label: 'SRS', color: 'var(--mode-srs)' },
    { key: 'hidden', label: 'Скрытые', color: 'var(--theme-text-muted)' },
  ];

  return (
    <Layout>
      {/* Undo тосты */}
      <div style={{ position: 'fixed', bottom: 80, left: 0, right: 0, zIndex: 200, display: 'flex', flexDirection: 'column', gap: 8, padding: '0 16px', pointerEvents: 'none' }}>
        {Array.from(pendingDeletions.values()).map(pd => (
          <div
            key={pd.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 16px',
              background: 'var(--theme-bg-elevated)',
              border: '1px solid var(--theme-border)',
              borderRadius: 12,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              pointerEvents: 'all',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--theme-text)' }}>
              «{pd.word.original_text}» удалено
            </span>
            <button
              onClick={() => handleUndoDelete(pd.id)}
              style={{
                padding: '4px 12px',
                background: 'var(--theme-primary)',
                color: 'var(--theme-primary-text)',
                border: 'none',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Отменить
            </button>
          </div>
        ))}
      </div>

      <div style={{ padding: '16px 16px 16px 4px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, color: 'var(--theme-text)' }}>Мои слова</h1>
            {wordsData && (
              <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginTop: 2 }}>
                {selectionMode ? `Выбрано: ${selectedIds.size} из ${wordsData.total}` : `${wordsData.total} слов`}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {selectionMode ? (
              <>
                <button
                  onClick={handleSelectAll}
                  style={{
                    padding: '8px 12px',
                    background: 'var(--theme-bg-elevated)',
                    color: 'var(--theme-text-secondary)',
                    border: '1px solid var(--theme-border)',
                    borderRadius: 10, fontSize: 13, cursor: 'pointer',
                  }}
                >
                  {selectedIds.size === (wordsData?.words.length ?? 0) ? 'Снять' : 'Все'}
                </button>
                <button
                  onClick={() => { setSelectionMode(false); setSelectedIds(new Set()); setBulkFolderPicker(false); }}
                  style={{
                    padding: '8px 12px',
                    background: 'var(--theme-bg-elevated)',
                    color: 'var(--theme-text-secondary)',
                    border: '1px solid var(--theme-border)',
                    borderRadius: 10, fontSize: 13, cursor: 'pointer',
                  }}
                >
                  Отмена
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setSelectionMode(true)}
                  style={{
                    padding: '10px 12px',
                    background: 'var(--theme-bg-elevated)',
                    color: 'var(--theme-text-secondary)',
                    border: '1px solid var(--theme-border)',
                    borderRadius: 10, fontSize: 13, cursor: 'pointer',
                    minHeight: '44px',
                  }}
                >
                  ☑
                </button>
                <button
                  onClick={() => { setEditWord(null); setShowModal(true); }}
                  style={{
                    padding: '10px 16px',
                    background: 'var(--theme-primary)',
                    color: 'var(--theme-primary-text)',
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: '44px',
                  }}
                >
                  + Добавить
                </button>
              </>
            )}
          </div>
        </div>

        {/* Search */}
        <div style={{ marginBottom: 10, position: 'relative' }}>
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => {
              setShowFolderDropdown(false);
              window.focus();
            }}
            onClick={() => {
              searchInputRef.current?.focus();
              window.focus();
            }}
            placeholder="🔍 Поиск слов..."
            style={{
              width: '100%',
              padding: search ? '10px 36px 10px 12px' : '10px 12px',
              background: 'var(--theme-bg-elevated)',
              border: '1px solid var(--theme-border)',
              borderRadius: 10,
              color: 'var(--theme-text)',
              fontSize: 15,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          {search && (
            <button
              onClick={() => {
                setSearch('');
                setSearchQuery('');
                searchInputRef.current?.focus();
              }}
              style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: 'var(--theme-text-muted)',
                fontSize: 16,
                cursor: 'pointer',
                padding: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Очистить поиск"
            >
              ✕
            </button>
          )}
        </div>

        {/* Chip-фильтры: режимы */}
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 8, paddingBottom: 2, scrollbarWidth: 'none' }}>
          {filterChips.map(chip => (
            <button
              key={chip.key}
              onClick={() => setFilterMode(chip.key)}
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                border: `1px solid ${filterMode === chip.key ? chip.color : 'var(--theme-border)'}`,
                background: filterMode === chip.key ? `${chip.color}20` : 'var(--theme-bg-elevated)',
                color: filterMode === chip.key ? chip.color : 'var(--theme-text-secondary)',
                fontSize: 13,
                fontWeight: filterMode === chip.key ? 600 : 400,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* Папки: dropdown + управление */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => setShowFolderDropdown(p => !p)}
              style={{
                padding: '6px 12px',
                borderRadius: 999,
                border: '1px solid var(--theme-border)',
                background: selectedFolderId ? 'var(--theme-primary)20' : 'var(--theme-bg-elevated)',
                color: selectedFolderId ? 'var(--theme-primary)' : 'var(--theme-text-secondary)',
                fontSize: 12,
                fontWeight: selectedFolderId ? 600 : 400,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              📁 {selectedFolderId
                ? `${folders.find(f => f.id === selectedFolderId)?.name ?? 'Папка'} (${folders.find(f => f.id === selectedFolderId)?.word_count ?? 0}) ▾`
                : `Папки (${folders.length}) ▾`}
            </button>
            {showFolderDropdown && (
              <>
                <div
                  onClick={() => setShowFolderDropdown(false)}
                  style={{ position: 'fixed', inset: 0, zIndex: 140 }}
                />
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  zIndex: 150,
                  minWidth: 230,
                maxWidth: 'calc(100vw - 32px)',
                background: 'var(--theme-bg-card)',
                border: '1px solid var(--theme-border)',
                borderRadius: 12,
                padding: '6px 0',
                boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'calc(100vh - 180px)',
              }}>
                {/* Все слова */}
                <button
                  onClick={() => { setSelectedFolderId(null); setShowFolderDropdown(false); }}
                  style={{
                    width: '100%',
                    padding: '8px 14px',
                    background: selectedFolderId === null ? 'var(--theme-bg-elevated)' : 'transparent',
                    border: 'none',
                    color: selectedFolderId === null ? 'var(--theme-primary)' : 'var(--theme-text)',
                    fontSize: 13,
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <span>Все слова</span>
                  <span style={{ color: 'var(--theme-text-muted)', fontSize: 11 }}>{wordsData?.total ?? 0}</span>
                </button>

                {/* Разделитель */}
                {folders.length > 0 && <div style={{ height: 1, background: 'var(--theme-border)', margin: '4px 0', flexShrink: 0 }} />}

                {/* Прокручиваемый список папок */}
                {folders.length > 0 && (
                  <div style={{
                    maxHeight: 190,
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    WebkitOverflowScrolling: 'touch',
                  }}>
                    {folders.map(f => (
                      <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <button
                          onClick={() => { setSelectedFolderId(selectedFolderId === f.id ? null : f.id); setShowFolderDropdown(false); }}
                          style={{
                            flex: 1,
                            padding: '8px 14px',
                            background: selectedFolderId === f.id ? 'var(--theme-bg-elevated)' : 'transparent',
                            border: 'none',
                            color: selectedFolderId === f.id ? 'var(--theme-primary)' : 'var(--theme-text)',
                            fontSize: 13,
                            textAlign: 'left',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📁 {f.name}</span>
                          <span style={{ color: 'var(--theme-text-muted)', fontSize: 11, marginLeft: 8 }}>{f.word_count}</span>
                        </button>
                        {f.word_count > 0 && (
                          <button
                            onClick={() => {
                              setShowFolderDropdown(false);
                              navigate(`/study?folder_id=${f.id}&folder_name=${encodeURIComponent(f.name)}`);
                            }}
                            style={{
                              padding: '4px 8px',
                              marginRight: 8,
                              background: 'none',
                              border: 'none',
                              color: 'var(--theme-primary)',
                              fontSize: 12,
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            → Учить
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Разделитель + фиксированные действия внизу */}
                <div style={{ height: 1, background: 'var(--theme-border)', margin: '4px 0', flexShrink: 0 }} />
                <div style={{ flexShrink: 0 }}>
                  <button
                    onClick={() => {
                      setShowCreateFolderModal(true);
                      setCreateFolderName('');
                      setShowFolderDropdown(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 14px',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--theme-primary)',
                      fontSize: 13,
                      fontWeight: 500,
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span>➕</span>
                    <span>Создать папку</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowFolderDropdown(false);
                      handleAddTop100();
                    }}
                    disabled={addingTop100}
                    style={{
                      width: '100%',
                      padding: '8px 14px',
                      background: 'transparent',
                      border: 'none',
                      color: '#3b82f6',
                      fontSize: 13,
                      fontWeight: 500,
                      textAlign: 'left',
                      cursor: addingTop100 ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span>{addingTop100 ? '⏳' : '📚'}</span>
                    <span>{addingTop100 ? 'Добавляем Top 100...' : 'Добавить курс Top 100 (A1)'}</span>
                  </button>
                  <button
                    onClick={() => { setShowFolderManager(true); setShowFolderDropdown(false); }}
                    style={{
                      width: '100%',
                      padding: '8px 14px',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--theme-text-secondary)',
                      fontSize: 12,
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span>⚙️</span>
                    <span>Управление папками</span>
                  </button>
                </div>
              </div>
            </>
          )}
          </div>
          {selectedFolderId && (
            <>
              <button
                onClick={() => setSelectedFolderId(null)}
                style={{
                  padding: '6px 10px',
                  borderRadius: 999,
                  border: '1px solid var(--theme-border)',
                  background: 'var(--theme-bg-elevated)',
                  color: 'var(--theme-text-muted)',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </>
          )}
        </div>

        {/* Кнопка "Учить" для выбранной папки */}
        {selectedFolderId && (
          <button
            onClick={() => {
              const f = folders.find(x => x.id === selectedFolderId);
              if (f) navigate(`/study?folder_id=${f.id}&folder_name=${encodeURIComponent(f.name)}`);
            }}
            style={{
              width: '100%',
              padding: '12px',
              marginBottom: 12,
              background: 'var(--theme-primary)',
              color: 'var(--theme-primary-text)',
              border: 'none',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            📚 Учить {declensionWords(folders.find(f => f.id === selectedFolderId)?.word_count ?? 0)} из папки «{folders.find(f => f.id === selectedFolderId)?.name ?? 'Папка'}» →
          </button>
        )}

        {/* Менеджер папок (открывается из dropdown) */}
        {showFolderManager && (
          <div style={{
            marginBottom: 12,
            padding: '12px',
            background: 'var(--theme-bg-card)',
            border: '1px solid var(--theme-border)',
            borderRadius: 10,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
                Управление папками
              </div>
              <button
                onClick={() => setShowFolderManager(false)}
                style={{ background: 'none', border: 'none', color: 'var(--theme-text-muted)', fontSize: 16, cursor: 'pointer', padding: '0 4px' }}
              >
                ✕
              </button>
            </div>
            {/* Список существующих папок */}
            {folders.map(f => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--theme-border)', gap: 6 }}>
                {editingFolderId === f.id ? (
                  <input
                    autoFocus
                    value={editingFolderName}
                    onChange={e => setEditingFolderName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      } else if (e.key === 'Escape') {
                        setEditingFolderId(null);
                      }
                    }}
                    onBlur={() => handleRenameFolder(f.id)}
                    maxLength={50}
                    style={{ flex: 1, padding: '3px 8px', background: 'var(--theme-bg-elevated)', border: '1px solid var(--theme-primary)', borderRadius: 6, color: 'var(--theme-text)', fontSize: 13, outline: 'none' }}
                  />
                ) : (
                  <button
                    onClick={() => {
                      setSelectedFolderId(f.id);
                      setShowFolderManager(false);
                    }}
                    style={{
                      flex: 1,
                      background: 'none',
                      border: 'none',
                      padding: '4px 0',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                    title="Открыть папку"
                  >
                    <span style={{ fontSize: 13, color: selectedFolderId === f.id ? 'var(--theme-primary)' : 'var(--theme-text)', fontWeight: selectedFolderId === f.id ? 600 : 400 }}>
                      📁 {f.name}
                    </span>
                    <span style={{ color: 'var(--theme-text-muted)', fontSize: 11 }}>
                      ({f.word_count} сл.)
                    </span>
                    {selectedFolderId === f.id && (
                      <span style={{ fontSize: 11, color: 'var(--theme-primary)', fontWeight: 600 }}>✓ активна</span>
                    )}
                  </button>
                )}
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <button
                    onClick={() => {
                      setSelectedFolderId(f.id);
                      setShowFolderManager(false);
                    }}
                    title="Перейти в папку"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--theme-primary)',
                      cursor: 'pointer',
                      fontSize: 12,
                      padding: '2px 6px',
                      fontWeight: 500,
                    }}
                  >
                    Открыть →
                  </button>
                  <button
                    onClick={() => { setEditingFolderId(f.id); setEditingFolderName(f.name); }}
                    title="Переименовать"
                    style={{ background: 'none', border: 'none', color: 'var(--theme-text-secondary)', cursor: 'pointer', fontSize: 13, padding: '2px 4px' }}
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => setDeletingFolder(f)}
                    title="Удалить"
                    style={{ background: 'none', border: 'none', color: 'var(--theme-error)', cursor: 'pointer', fontSize: 13, padding: '2px 6px' }}
                  >
                    🗑
                  </button>
                </div>
              </div>
            ))}
            {/* Создание новой папки */}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input
                type="text"
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateFolder()}
                placeholder="Название папки..."
                maxLength={50}
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  background: 'var(--theme-bg-elevated)',
                  border: '1px solid var(--theme-border)',
                  borderRadius: 8,
                  color: 'var(--theme-text)',
                  fontSize: 13,
                  outline: 'none',
                }}
              />
              <button
                onClick={() => handleCreateFolder()}
                disabled={creatingFolder || !newFolderName.trim()}
                style={{
                  padding: '8px 14px',
                  background: 'var(--theme-primary)',
                  color: 'var(--theme-primary-text)',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: creatingFolder ? 'not-allowed' : 'pointer',
                  opacity: !newFolderName.trim() ? 0.5 : 1,
                }}
              >
                {creatingFolder ? '...' : 'Создать'}
              </button>
            </div>

            {/* Добавить курс Top 100 */}
            <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--theme-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)' }}>
                Базовый словарный набор:
              </div>
              <button
                onClick={handleAddTop100}
                disabled={addingTop100}
                style={{
                  padding: '6px 12px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: 8,
                  color: '#3b82f6',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: addingTop100 ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{addingTop100 ? '⏳' : '📚'}</span>
                <span>{addingTop100 ? 'Загрузка...' : 'Добавить Top 100 (A1)'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Сортировка */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {[
            { field: 'created_at', label: 'По дате' },
            { field: 'original_text', label: 'А-Я' },
            { field: 'srs_level', label: 'SRS' },
            { field: 'next_review_at', label: 'По повторению' },
          ].map(s => (
            <button
              key={s.field}
              onClick={() => toggleSort(s.field)}
              style={{
                padding: '4px 10px',
                borderRadius: 8,
                border: '1px solid var(--theme-border)',
                background: sort === s.field ? 'var(--theme-bg-elevated)' : 'transparent',
                color: sort === s.field ? 'var(--theme-text)' : 'var(--theme-text-muted)',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              {s.label} {sort === s.field ? (order === 'asc' ? '↑' : '↓') : ''}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading && !wordsData ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--theme-text-secondary)' }}>
            Загрузка слов...
          </div>
        ) : !wordsData || wordsData.words.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: 'var(--theme-text-secondary)',
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📝</div>
            {search ? (
              <>
                <div style={{ fontSize: 16, marginBottom: 8 }}>Слова не найдены</div>
                <div style={{ fontSize: 13 }}>Попробуйте другой запрос</div>
              </>
            ) : filterMode !== 'all' ? (
              <>
                <div style={{ fontSize: 16, marginBottom: 8 }}>Нет слов в этом фильтре</div>
                <div style={{ fontSize: 13 }}>Попробуйте другой фильтр</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 16, marginBottom: 8 }}>Словарь пустой</div>
                <div style={{ fontSize: 13, marginBottom: 20 }}>
                  Не знаешь с чего начать? Давай добавим 100 самых распространённых слов!
                </div>
                <button
                  onClick={handleAddTop100}
                  disabled={addingTop100}
                  style={{
                    padding: '12px 24px',
                    background: addingTop100 ? 'var(--theme-border)' : 'var(--theme-primary)',
                    color: 'var(--theme-primary-text)',
                    border: 'none',
                    borderRadius: 12,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: addingTop100 ? 'not-allowed' : 'pointer',
                  }}
                >
                  {addingTop100 ? '⏳ Добавляю...' : '🚀 Давай!'}
                </button>
              </>
            )}
          </div>
        ) : (
          <div>
            {wordsData.words.map((word, idx) => (
              <SwipeActions
                key={word.id}
                enabled={!selectionMode}
                onHide={() => handleToggleHidden(word.id, !word.is_hidden)}
                onEdit={() => handleEdit(word)}
                onDelete={() => handleDelete(word.id)}
              >
                <WordCard
                  word={word}
                  index={idx + 1}
                  expanded={expandedWordId === word.id}
                  onToggleExpand={() => setExpandedWordId(expandedWordId === word.id ? null : word.id)}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleHidden={handleToggleHidden}
                  onReset={handleReset}
                  folders={folders}
                  onFolderToggle={handleFolderToggle}
                  mcReps={mcReps}
                  selectionMode={selectionMode}
                  selected={selectedIds.has(word.id)}
                  onSelect={handleSelectWord}
                  searchQuery={searchQuery}
                  onOpenCreateFolder={() => {
                    setShowCreateFolderModal(true);
                    setCreateFolderName('');
                  }}
                />
              </SwipeActions>
            ))}
            {/* Sentinel для подгрузки */}
            {hasMore && (
              <div ref={loadMoreRef} style={{ padding: 16, textAlign: 'center', color: 'var(--theme-text-muted)', fontSize: 13 }}>
                {loadingMore ? 'Загрузка...' : `Показано ${wordsData.words.length} из ${wordsData.total}`}
              </div>
            )}
          </div>
        )}
      </div>

      {showModal && (
        <AddWordModal
          onClose={() => { setShowModal(false); setEditWord(null); }}
          onSaved={() => {
            loadWords();
            loadFolders();
            setTimeout(() => {
              loadWords();
              loadFolders();
            }, 3500);
          }}
          editWord={editWord}
          isDesktop={isDesktop}
          folderId={!editWord ? selectedFolderId : null}
          folderName={!editWord ? (folders.find(f => f.id === selectedFolderId)?.name ?? null) : null}
          pendingDeletionIds={new Set(pendingDeletions.keys())}
        />
      )}

      {/* Диалог удаления папки */}
      {deletingFolder && (
        <div
          onClick={() => setDeletingFolder(null)}
          style={{ position: 'fixed', inset: 0, background: 'var(--theme-bg-overlay)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--theme-bg-elevated)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 340 }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--theme-text)', marginBottom: 8 }}>
              Удалить папку «{deletingFolder.name}»?
            </div>
            {deletingFolder.word_count > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
                <button
                  onClick={() => handleDeleteFolderConfirm(deletingFolder, true)}
                  style={{
                    padding: '12px 14px',
                    background: 'var(--theme-bg)',
                    border: '1px solid var(--theme-border)',
                    borderRadius: 12,
                    fontSize: 14,
                    color: 'var(--theme-text)',
                    cursor: 'pointer',
                    fontWeight: 500,
                    textAlign: 'center',
                  }}
                >
                  Удалить папку и её слова
                  <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginTop: 2, fontWeight: 400 }}>
                    Удалятся папка и слова из этой папки
                  </div>
                </button>
                <button
                  onClick={() => handleDeleteFolderConfirm(deletingFolder, false)}
                  style={{
                    padding: '12px 14px',
                    background: 'var(--theme-bg)',
                    border: '1px solid var(--theme-border)',
                    borderRadius: 12,
                    fontSize: 14,
                    color: 'var(--theme-text)',
                    cursor: 'pointer',
                    fontWeight: 500,
                    textAlign: 'center',
                  }}
                >
                  Удалить только папку
                  <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginTop: 2, fontWeight: 400 }}>
                    Слова останутся в словаре
                  </div>
                </button>
                <button
                  onClick={() => setDeletingFolder(null)}
                  style={{
                    padding: '10px',
                    background: 'transparent',
                    border: 'none',
                    fontSize: 14,
                    color: 'var(--theme-text-muted)',
                    cursor: 'pointer',
                    marginTop: 2,
                  }}
                >
                  Отмена
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)', marginBottom: 20 }}>
                  Папка пустая. Вы уверены, что хотите удалить её?
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => setDeletingFolder(null)}
                    style={{ flex: 1, padding: '12px', background: 'transparent', border: '1px solid var(--theme-border)', borderRadius: 10, fontSize: 14, color: 'var(--theme-text-secondary)', cursor: 'pointer' }}
                  >
                    Отмена
                  </button>
                  <button
                    onClick={() => handleDeleteFolderConfirm(deletingFolder, false)}
                    style={{ flex: 1, padding: '12px', background: 'var(--theme-error)', border: 'none', borderRadius: 10, fontSize: 14, color: '#fff', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Удалить
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Модалка создания папки */}
      {showCreateFolderModal && (
        <div
          onClick={() => setShowCreateFolderModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'var(--theme-bg-overlay, rgba(0,0,0,0.7))',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            backdropFilter: 'blur(3px)',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--theme-bg-elevated)',
              border: '1px solid var(--theme-border)',
              borderRadius: 16,
              padding: 20,
              width: '100%',
              maxWidth: 360,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--theme-text)' }}>
                📁 Новая папка
              </div>
              <button
                onClick={() => setShowCreateFolderModal(false)}
                style={{ background: 'none', border: 'none', color: 'var(--theme-text-muted)', fontSize: 18, cursor: 'pointer', padding: 4 }}
              >
                ✕
              </button>
            </div>

            {/* Быстрые эмодзи-шаблоны */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {[
                { emoji: '✈️', label: 'Путешествия' },
                { emoji: '💼', label: 'Работа' },
                { emoji: '🎬', label: 'Фильмы' },
                { emoji: '📚', label: 'Учёба' },
                { emoji: '🍔', label: 'Еда' },
                { emoji: '⭐', label: 'Важное' },
              ].map(preset => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => setCreateFolderName(`${preset.emoji} ${preset.label}`)}
                  style={{
                    padding: '4px 8px',
                    borderRadius: 8,
                    background: 'var(--theme-bg)',
                    border: '1px solid var(--theme-border)',
                    color: 'var(--theme-text-secondary)',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  {preset.emoji} {preset.label}
                </button>
              ))}
            </div>

            <input
              type="text"
              autoFocus
              value={createFolderName}
              onChange={e => setCreateFolderName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && createFolderName.trim() && !creatingFolder) {
                  handleCreateFolder(createFolderName);
                }
              }}
              placeholder="Например: IT или Сериалы..."
              maxLength={50}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'var(--theme-bg)',
                border: '1px solid var(--theme-border)',
                borderRadius: 10,
                color: 'var(--theme-text)',
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box',
                marginBottom: 16,
              }}
            />

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setShowCreateFolderModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  border: '1px solid var(--theme-border)',
                  borderRadius: 10,
                  color: 'var(--theme-text-secondary)',
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                disabled={creatingFolder || !createFolderName.trim()}
                onClick={() => handleCreateFolder(createFolderName)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'var(--theme-primary)',
                  border: 'none',
                  borderRadius: 10,
                  color: 'var(--theme-primary-text)',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: creatingFolder || !createFolderName.trim() ? 'not-allowed' : 'pointer',
                  opacity: !createFolderName.trim() ? 0.5 : 1,
                }}
              >
                {creatingFolder ? 'Создаю...' : 'Создать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selectionMode && selectedIds.size > 0 && (
        <div style={{
          position: 'fixed', bottom: 80, left: 0, right: 0, zIndex: 200,
          padding: '0 16px', pointerEvents: 'none',
        }}>
          <div style={{
            background: 'var(--theme-bg-elevated)',
            border: '1px solid var(--theme-border)',
            borderRadius: 14,
            padding: '12px 16px',
            display: 'flex', gap: 8, alignItems: 'center',
            boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
            pointerEvents: 'all',
          }}>
            <span style={{ fontSize: 13, color: 'var(--theme-text-secondary)', flex: 1 }}>
              Выбрано: {selectedIds.size}
            </span>
            <button
              onClick={handleBulkReset}
              style={{
                padding: '8px 14px', background: 'rgba(255, 152, 0, 0.1)', border: '1px solid var(--theme-warning)',
                borderRadius: 8, fontSize: 13, color: 'var(--theme-warning)', cursor: 'pointer', fontWeight: 600,
              }}
            >
              🔄 Сбросить
            </button>
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setBulkFolderPicker(p => !p)}
                style={{
                  padding: '8px 14px', background: 'var(--theme-primary)20', border: '1px solid var(--theme-primary)',
                  borderRadius: 8, fontSize: 13, color: 'var(--theme-primary)', cursor: 'pointer', fontWeight: 600,
                }}
              >
                📁 В папку
              </button>
              {bulkFolderPicker && (
                <div style={{
                  position: 'absolute', bottom: '110%', right: 0, background: 'var(--theme-bg-elevated)',
                  border: '1px solid var(--theme-border)', borderRadius: 10, padding: 8, minWidth: 160,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.3)', zIndex: 10,
                }}>
                  {folders.length === 0 && (
                    <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', padding: '4px 8px' }}>Нет папок</div>
                  )}
                  {folders.map(f => (
                    <button
                      key={f.id}
                      onClick={() => handleBulkAddToFolder(f.id)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '8px 10px', background: 'transparent', border: 'none',
                        borderRadius: 6, fontSize: 13, color: 'var(--theme-text)', cursor: 'pointer',
                      }}
                    >
                      📁 {f.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={handleBulkDelete}
              style={{
                padding: '8px 14px', background: 'rgba(255,82,82,0.1)', border: '1px solid var(--theme-error)',
                borderRadius: 8, fontSize: 13, color: 'var(--theme-error)', cursor: 'pointer', fontWeight: 600,
              }}
            >
              🗑 Удалить
            </button>
          </div>
        </div>
      )}
      {/* Toast ошибок */}
      {errorToast && (
        <div style={{
          position: 'fixed',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--theme-error, #dc2626)',
          color: '#fff',
          padding: '10px 18px',
          borderRadius: 12,
          fontSize: 14,
          fontWeight: 600,
          zIndex: 10000,
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          maxWidth: 'calc(100vw - 32px)',
        }}>
          <span>⚠️</span>
          <span>{errorToast}</span>
        </div>
      )}

      {/* Модалка подтверждения добавления курса Top 100 */}
      {showTop100Modal && (
        <div
          onClick={() => setShowTop100Modal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'var(--theme-bg-overlay, rgba(0,0,0,0.7))',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            backdropFilter: 'blur(3px)',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--theme-bg-elevated)',
              border: '1px solid var(--theme-border)',
              borderRadius: 16,
              padding: 20,
              width: '100%',
              maxWidth: 360,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--theme-text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>📚</span>
              <span>Курс Top 100 (A1)</span>
            </div>
            <div style={{ fontSize: 14, color: 'var(--theme-text-secondary)', lineHeight: 1.5, marginBottom: 20 }}>
              Добавить 100 базовых слов (A1) в отдельную папку «📚 Top 100 (A1)»?
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowTop100Modal(false)}
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: '1px solid var(--theme-border)',
                  background: 'transparent',
                  color: 'var(--theme-text)',
                  fontSize: 14,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Отмена
              </button>
              <button
                onClick={executeAddTop100}
                disabled={addingTop100}
                style={{
                  padding: '10px 18px',
                  borderRadius: 10,
                  border: 'none',
                  background: 'var(--theme-primary)',
                  color: 'var(--theme-primary-text, #fff)',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: addingTop100 ? 'not-allowed' : 'pointer',
                }}
              >
                {addingTop100 ? 'Добавляем...' : 'Добавить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка сброса прогресса одного слова */}
      {resettingWord && (
        <div
          onClick={() => setResettingWord(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'var(--theme-bg-overlay, rgba(0,0,0,0.7))',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            backdropFilter: 'blur(3px)',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--theme-bg-elevated)',
              border: '1px solid var(--theme-border)',
              borderRadius: 16,
              padding: 20,
              width: '100%',
              maxWidth: 360,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--theme-text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>🔄</span>
              <span>Сбросить прогресс</span>
            </div>
            <div style={{ fontSize: 14, color: 'var(--theme-text-secondary)', lineHeight: 1.5, marginBottom: 20 }}>
              Сбросить прогресс слова «{resettingWord.original_text}» в самое начало (режим выбора)?
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setResettingWord(null)}
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: '1px solid var(--theme-border)',
                  background: 'transparent',
                  color: 'var(--theme-text)',
                  fontSize: 14,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Отмена
              </button>
              <button
                onClick={() => executeResetWord(resettingWord.id)}
                style={{
                  padding: '10px 18px',
                  borderRadius: 10,
                  border: 'none',
                  background: 'var(--theme-warning, #f59e0b)',
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Сбросить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка пакетного сброса прогресса */}
      {showBulkResetModal && (
        <div
          onClick={() => setShowBulkResetModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'var(--theme-bg-overlay, rgba(0,0,0,0.7))',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            backdropFilter: 'blur(3px)',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--theme-bg-elevated)',
              border: '1px solid var(--theme-border)',
              borderRadius: 16,
              padding: 20,
              width: '100%',
              maxWidth: 360,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--theme-text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>🔄</span>
              <span>Сбросить прогресс</span>
            </div>
            <div style={{ fontSize: 14, color: 'var(--theme-text-secondary)', lineHeight: 1.5, marginBottom: 20 }}>
              Сбросить прогресс {selectedIds.size} выбранных слов в самое начало (режим выбора)?
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowBulkResetModal(false)}
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: '1px solid var(--theme-border)',
                  background: 'transparent',
                  color: 'var(--theme-text)',
                  fontSize: 14,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Отмена
              </button>
              <button
                onClick={executeBulkReset}
                style={{
                  padding: '10px 18px',
                  borderRadius: 10,
                  border: 'none',
                  background: 'var(--theme-warning, #f59e0b)',
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Сбросить
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
